<template>
  <div class="card overflow-hidden">
    <div class="card-header">
      <div class="d-flex justify-content-between">
        <div>
          <div class="mb-0 text-xs header-sm mb-1">ALL SECTIONS</div>
          <div class="d-flex flex-row justify-content-between align-items-center">
            <div id="performanceTable" class="title-header">{{ title }}</div>
            <div v-if="title != 'Performance'" class="ml-0 reset-filter" @click="resetFilter">Reset</div>
          </div>
        </div>
        <div class="d-flex form-inline">
          <div class="form-group justify-content-around">
            <PlatoonDropdown 
              id="ddPlatoon" 
              :allow-all="isAdminRole" :selected="selectedPlatoon" 
              @selected-platoon-id="(selectedValue) => (selectedPlatoon = selectedValue)" 
              />

            <SectionDropdown 
              id="ddSectionFilterPerformance" 
              style="margin-left: 1rem;" 
              :selected="selectedSection" 
              @selected-section-id="(selectedValue) => (selectedSection = selectedValue)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="card-body">
      <div class="table-responsive">
        <el-table ref="dataTable" :data="data.rows" style="width: 100%" @sort-change="sortColumn">
          <el-table-column prop="name" label="Name" width="320" sortable="custom">
            <template #default="scope">
              <span v-if="scope.row.user && scope.row.user.length > 0">
                {{ scope.row.user[0].name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="platoon_name" label="Platoon" width="160" sortable="custom">
            <template #default="scope">
              <span 
                v-if="scope.row.user && scope.row.user.length > 0" 
                :title="scope.row.user[0].platoon_name" 
                class="truncate-text" >
                {{ scope.row.user[0].platoon_name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="section_name" label="Section" width="160" sortable="custom">
            <template #default="scope">
              <span v-if="scope.row.user && scope.row.user.length > 0">
                {{ scope.row.user[0].section_name }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="strain" label="Strain" width="240" sortable="custom" class-name="pe-3">
            <template #default="scope">
              <argon-progress 
                :percentage="(scope.row.strain / 21) * 100" 
                :left-text="`${scope.row.strain}`"
                :left-sub-text="`${scope.row.previous_strain}`" 
                />
            </template>
          </el-table-column>
          <el-table-column prop="recovery_score" label="Recovery" width="240" sortable="custom" class-name="pe-3">
            <template #default="scope">
              <argon-progress 
                :color="getRecoveryScoreColor(scope.row.recovery_score)"
                :percentage="scope.row.recovery_score"
                :left-text="`${scope.row.recovery_score > -1 ? scope.row.recovery_score : '--'}`"
                :left-sub-text="`${scope.row.previous_recovery_score > -1 ? scope.row.previous_recovery_score : '--'}`"
                :title="scope.row.recovery_score > -1 ? scope.row.recovery_score : 'Calibration in progress'" 
              />
            </template>
          </el-table-column>
          <el-table-column prop="sleep_score" label="Sleep" width="240" sortable="custom" class-name="pe-3">
            <template #default="scope">
              <argon-progress 
                :color="getSleepScoreColor(scope.row.sleep_score)" :percentage="scope.row.sleep_score"
                :left-text="`${scope.row.sleep_score}`" :left-sub-text="`${scope.row.previous_sleep_score}`"
                :right-text="parseMinutesToSleepHours(scope.row.sleep_minutes)" 
              />
            </template>
          </el-table-column>
          <el-table-column prop="workouts" label="Workouts" width="160">
            <template #default="scope">
              <div v-if="scope.row.workouts" class="d-flex">
                <div v-for="(workout, idx) in scope.row.workouts" :key="workout.workout_record_id">
                  <el-popover effect="light" trigger="hover" placement="top" width="auto">
                    <template #default>
                      <div class="container">
                        <div class="header">
                          <div class="title">{{ getWorkoutName(workout.workout_type) }}</div>
                        </div>
                        <div class="time-range">{{ formatUnixTimeStamp(workout.start_time) }} - {{
                          formatUnixTimeStamp(workout.end_time) }}</div>
                        <div class="stats">
                          <div class="stat">
                            <icon-duration></icon-duration>
                            <div class="details">{{ formatDurationInSec(workout.total_time) }}</div>
                          </div>
                          <div class="stat">
                            <icon-calories></icon-calories>
                            <div class="details">{{ workout.calories }} kcal</div>
                          </div>
                          <div v-if="distanceWorkoutTypes.includes(workout.workout_type)" class="stat">
                            <icon-location></icon-location>
                            <div class="details">{{ workout.workout_distance / 1000 }} KM</div>
                          </div>
                          <div class="stat">
                            <icon-vitals></icon-vitals>
                            <div class="details">{{ workout.min_hr }} - {{ workout.max_hr }} bpm</div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template #reference>
                      <img 
                        v-if="idx < maxWorkouts" :src="getWorkoutIcon(workout.workout_type)"
                        onerror="this.onerror=null; this.src='/img/icons/ic_workout_255.svg'"
                        :alt="getWorkoutName(workout.workout_type)" class="me-2" />
                    </template>
                  </el-popover>
                </div>
                <span 
                  v-if="scope.row.workouts && scope.row.workouts.length > maxWorkouts"
                >
                  +{{ scope.row.workouts.length - maxWorkouts }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="" width="60">
            <template #default="scope">
              <div class="d-flex">
                <router-link 
                  target="_blank" :to="{
                  name: `User`,
                  params: { udid: scope.row.udid },
                  query: { date: getDate() },
                }" exact>
                  <icon-user-info />
                </router-link>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <div class="card-footer paginator">
      <div v-if="data && data.count > 0" class="d-flex justify-content-between align-items-center">
        <span class="ms-3 text-muted">{{ data.count }} record(s)</span>
        <app-pagination 
          :records="data.count" 
          :page-size="pageSize" 
          :page="page" 
          @update-page-selection="(selectedValue) => updatePageSelection(selectedValue)"
         />
      </div>
    </div>
  </div>
</template>

<script>
import Constants from "@/helpers/constants";
import Utils from "@/helpers/utils";
import DateUtils from "@/helpers/dateUtils";

import SectionDropdown from "../../components/SectionDropdown.vue";
import PlatoonDropdown from "../../components/PlatoonDropdown.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import IconUserInfo from "@/shared/Controls/Icons/IconUserInfo.vue";
import IconCalories from "@/shared/Controls/Icons/IconCalories.vue";
import IconDuration from "@/shared/Controls/Icons/IconDuration.vue";
import IconLocation from "@/shared/Controls/Icons/IconLocation.vue";
import IconVitals from "@/shared/Controls/Icons/IconVitals.vue";
import { Tooltip } from "bootstrap/dist/js/bootstrap.esm.min.js";

import dayjs from "dayjs";
const utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export default {
  name: "PerformanceTable",
  components: {
    ArgonProgress,
    IconUserInfo,
    SectionDropdown,
    IconCalories,
    IconDuration,
    IconLocation,
    IconVitals,
    PlatoonDropdown,
  },
  props: {
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    selectedDate: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
      default: 'Performance'
    },
  },
  emits: [
    "selected-section-id",
    "selected-platoon-id",
    "selected-page",
    "selected-sort",
    "selected-order",
  ],
  data() {
    return {
      maxWorkouts: Constants.MAX_WORKOUTS,
      selectedSection: Constants.DEFAULT_SECTION,
      selectedPlatoon: -1,
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      sort: "name",
      order: "asc",
      filterApplied: false,
      distanceWorkoutTypes: Constants.DISTANCE_WORKOUT_TYPES,
      loggedInUser: null,
      isAdminRole: false,
    };
  },
  computed: {
    hasData() {
      return this.data && this.data.count > 0;
    },
  },
  watch: {
    selectedSection() {
      this.$emit("selected-section-id", this.selectedSection);
      this.updatePageSelection(1);
    },
    selectedPlatoon() {
      this.$emit("selected-platoon-id", this.selectedPlatoon);
      this.updatePageSelection(1);
    },
    selectedSectionId(newValue) {
      this.selectedSection = newValue;
    },
    selectedPlatoonId(newValue) {
      this.selectedPlatoon = newValue;
    },
    selectedDate() {
      this.updatePageSelection(1);
    },
  },
  mounted() {
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']",
    });
    
    this.loggedInUser = this.$store.getters["auth/getLoggedInUser"]();
    if (this.loggedInUser.role && this.loggedInUser.role.role_id == Constants.ROLES.PLATOON_COMMANDER) {
      this.selectedPlatoon = this.loggedInUser.platoon_id;
    }
    if (Constants.ADMIN_USER_ROLES.includes(this.loggedInUser.role.role_id)) {
       this.isAdminRole = true;
    }
  },
  methods: {
    sortColumn({ prop, order }) {
      this.$emit("selected-sort", prop);
      this.$emit("selected-order", order == "ascending" ? "asc" : "desc");
    },
    getSleepScoreColor(score) {
      return Utils.getSleepScoreColor(score);
    },
    getRecoveryScoreColor(score) {
      return Utils.getRecoveryScoreColor(score);
    },
    getWorkoutIcon(workoutType) {
      return Utils.getWorkoutIcon(workoutType);
    },
    getWorkoutName(workoutType) {
      const workoutTypeName =
        this.$store.getters["list/getWorkoutTypeName"](workoutType);

      return workoutTypeName;
    },
    parseMinutesToSleepHours(minutes) {
      return Utils.parseMinutesToSleepHours(minutes);
    },
    updatePageSelection(selectedPage) {
      this.page = selectedPage;
      this.$emit("selected-page", selectedPage);
    },
    getDate() {
      return DateUtils.formatDateToBucketDate(this.selectedDate);
    },
    resetFilter() {
      this.filterApplied = false;
      this.$parent.resetFilter();
    },
    formatUnixTimeStamp(unixTS) {
      let timeVal = dayjs(Utils.formatTimestamp(unixTS)).format(Constants.MDY_TS_FORMAT);
      return dayjs(timeVal).format(Constants.TIME_12_HRS_FORMAT);
    },
    formatDurationInSec(durationInSec) {
      return Utils.formatDurationInSec(durationInSec);
    },
  },
};
</script>

<style lang="scss" scoped>
.title-header {
  color: #fff !important;
  font-weight: 700 !important;
  font-size: 1rem !important;
}

.reset-filter {
  margin-left: 1rem;
  font-size: 1rem;
  color: orange !important;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
}

.container {
  background: transparent;
  color: white;
  padding: 0.6rem;
  // width: 300px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

.time-range {
  font-size: 0.8rem;
  color: #A0A0A0;
  margin-bottom: 1rem;
}

.stats {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat {
  display: flex;
  align-items: start;
  justify-content: left;
  margin-bottom: 0.25rem;
}

.icon {
  font-size: 1rem;
}

.details {
  font-size: 0.9rem;
  padding-left: 0.5rem;
}

.el-popper.is-light {
  background: rgba(0, 0, 0, 0.877) !important;
  border-radius: 0.5rem !important;
  padding: 0rem !important;
  margin: 0rem !important;
}
.truncate-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>